/**
 * LocationPicker State Machine
 * Finite state machine for managing picker state
 */

import type { PlaceData } from '@/types/location';

export type PickerMode = 'idle' | 'business' | 'address';

export interface PickerState {
  mode: PickerMode;
  place: PlaceData | null;
  previewAddress: string;
  isLoading: boolean;
  searchQuery: string;
  center: { lat: number; lng: number };
}

export type PickerAction =
  | { type: 'SET_BUSINESS_PLACE'; place: PlaceData }
  | { type: 'SET_ADDRESS'; place: PlaceData }
  | { type: 'SET_PREVIEW_ADDRESS'; address: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_SEARCH_QUERY'; query: string }
  | { type: 'SET_CENTER'; center: { lat: number; lng: number } }
  | { type: 'RESET'; initialCenter?: { lat: number; lng: number } };

export const createInitialState = (initialCenter?: { lat: number; lng: number }): PickerState => ({
  mode: 'idle',
  place: null,
  previewAddress: '',
  isLoading: false,
  searchQuery: '',
  center: initialCenter || { lat: 56.9496, lng: 24.1052 }, // Default to Riga
});

export const initialPickerState: PickerState = createInitialState();

export function pickerReducer(state: PickerState, action: PickerAction): PickerState {
  switch (action.type) {
    case 'SET_BUSINESS_PLACE':
      return {
        ...state,
        mode: 'business',
        place: action.place,
        previewAddress: '', // Clear preview when business is selected
        isLoading: false,
      };

    case 'SET_ADDRESS':
      return {
        ...state,
        mode: 'address',
        place: action.place,
        previewAddress: action.place.formatted_address || '',
        isLoading: false,
      };

    case 'SET_PREVIEW_ADDRESS':
      // Only update preview if not in business mode
      if (state.mode === 'business') {
        return state; // Don't update preview when business is selected
      }
      return {
        ...state,
        previewAddress: action.address,
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        mode: 'idle',
        place: null,
        previewAddress: '',
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.query,
      };

    case 'SET_CENTER':
      return {
        ...state,
        center: action.center,
      };

    case 'RESET':
      return createInitialState(action.initialCenter);

    default:
      return state;
  }
}

